import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import {
  fetchBookings,
  fetchRedemptionHistory,
  fetchRewardTypes,
  submitRedeemAward2,
  submitRedeemAwards
} from '../features/track-redeem/api/trackRedeemApi';
import { buildRedeemAward2Payload, buildRedeemAwardsPayload } from '../features/track-redeem/lib/redeemPayloadMapper';
import { getBookingRewardTypeOptions, rewardTypeRequiresGuestName } from '../features/track-redeem/lib/rewardTypeOptions';
import RedeemAwardRow from '../features/track-redeem/ui/RedeemAwardRow';
import SimpleTable from '../features/track-redeem/ui/SimpleTable';
import { getCurrentLanguageKey, getLanguageSection } from '../shared/services/languageService';

// Oculta temporalmente el bloque de condiciones de canje de noches gratis
// (redeem_a…redeem_h): por ahora no se ofrecen free nights. Poner en true para
// volver a mostrarlo; el texto y las traducciones siguen intactos.
const SHOW_FREE_NIGHTS_TERMS = false;

const inputClass = "h-11 w-full rounded-lg border border-slate-200 bg-white px-4 text-sm font-bold text-[#151b3f] outline-none transition placeholder:text-slate-400 focus:border-marival-blue focus:ring-4 focus:ring-marival-blue/10 disabled:bg-slate-100 disabled:text-slate-500";
const darkInputClass = "h-10 w-full rounded-lg border border-white/20 bg-white px-3 text-sm font-bold text-[#151b3f] outline-none transition placeholder:text-slate-400 focus:border-white focus:ring-4 focus:ring-white/15";

function createInitialRedeem() {
  return {
    RedeemFullName: Cookies.get('FirstName'),
    RedeemGuestName: '',
    RedeemEmail: '',
    Street: '',
    Apt: '',
    City: '',
    Province: '',
    Zipcode: '',
    Country: '',
    RedeemAwards: [],
    Curp: '',
    Rfc: '',
    BankName: '',
    BankClabe: '',
    BankAccount: '',
    BankBeneficiary: ''
  };
}

function getSessionTokens() {
  return {
    userAccessToken: Cookies.get('UserAccessToken'),
    deviceAccessToken: Cookies.get('DeviceAccessToken')
  };
}

function TrackRedeem() {
  const [bookings, setBookings] = useState([]);
  const [bookingsPagination, setBookingsPagination] = useState({
    page: 1,
    per_page: 0,
    results_in_page: 0,
    total_results: 0,
    total_pages: 0
  });
  const [rewardTypes, setRewardTypes] = useState([]);
  const [redemptionHistory, setRedemptionHistory] = useState([]);
  const [redemptionHistoryPage, setRedemptionHistoryPage] = useState(1);
  const [rows, setRows] = useState(0);
  const [pointsAvailable, setPointsAvailable] = useState(0);
  const [pointsPending, setPointsPending] = useState(0);
  const [isCash, setIsCash] = useState(false);
  const [currentBooking, setCurrentBooking] = useState(null);
  const [idMarivalRewardType, setIdMarivalRewardType] = useState(-1);
  const [redeem2DisabledButton, setRedeem2DisabledButton] = useState(false);
  const [redeem, setRedeem] = useState(createInitialRedeem);
  const [sendRewardsSuccess, setSendRewardsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redeemBoxError, setRedeemBoxError] = useState('');
  const [redeemBoxMessage, setRedeemBoxMessage] = useState('');
  const [errorMessage2, setErrorMessage2] = useState('');

  const language = getLanguageSection('trackredeem');
  const countryUser = Cookies.get('CountryUser');

  const loadBookings = (page = 1) => {
    fetchBookings({ page, ...getSessionTokens() })
      .then((result) => {
        setBookings(result.bookings);
        setPointsAvailable(result.pointsAvailable);
        setPointsPending(result.pointsPending);
        setBookingsPagination(result.pagination);
      })
      .catch(() => {});
  };

  const loadRewardTypes = () => {
    fetchRewardTypes(getSessionTokens())
      .then(setRewardTypes)
      .catch(() => setRewardTypes([]));
  };

  const loadRedemptionHistory = () => {
    fetchRedemptionHistory(getSessionTokens())
      .then(setRedemptionHistory)
      .catch(() => setRedemptionHistory([]));
  };

  useEffect(() => {
    loadBookings(1);
    loadRewardTypes();
    loadRedemptionHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearRedeemBoxMessages = () => {
    setRedeemBoxError('');
    setRedeemBoxMessage('');
  };

  const hideMessage = () => {
    clearRedeemBoxMessages();
    setErrorMessage2('');
    setSendRewardsSuccess(false);
  };

  const changeBookingsPage = (page) => {
    if (page === bookingsPagination.page) return;
    loadBookings(page);
  };

  const changeRedemptionHistoryPage = (page) => {
    if (page === redemptionHistoryPage) return;
    setRedemptionHistoryPage(page);
  };

  const handleAddAwardRow = () => {
    clearRedeemBoxMessages();
    setRows((current) => current + 1);
  };

  const handleRemoveAwardRow = (index) => {
    clearRedeemBoxMessages();
    setRows((current) => current - 1);
    setRedeem((current) => {
      const nextAwards = current.RedeemAwards.slice();
      nextAwards.splice(index, 1);
      return { ...current, RedeemAwards: nextAwards };
    });
  };

  const handleRedeemAwardChange = (awardData, index) => {
    clearRedeemBoxMessages();

    const nextAwards = redeem.RedeemAwards.slice();
    nextAwards[index] = awardData;
    const hasCash = nextAwards.some((award) => String(award.Id_MarivalRewardType) === '3');

    setRedeem((current) => ({ ...current, RedeemAwards: nextAwards }));
    setIsCash(hasCash);
  };

  const handleRedeemFieldChange = (event) => {
    hideMessage();
    const { name, value } = event.target;
    const [, field] = name.split('.');
    setRedeem((current) => ({ ...current, [field]: value }));
  };

  const handleSendRedeemAward = async () => {
    if (loading) return;

    clearRedeemBoxMessages();
    setLoading(true);

    const payload = buildRedeemAwardsPayload(redeem, {
      countryUser,
      lang: getCurrentLanguageKey(),
      ...getSessionTokens()
    });

    try {
      const response = await submitRedeemAwards(payload);
      setLoading(false);
      setRedeemBoxMessage(response.successMessage);
      loadBookings(bookingsPagination.page);
      loadRedemptionHistory();
    } catch (error) {
      setLoading(false);
      setRedeemBoxError(error?.payload?.errorMessage || error.message);
    }
  };

  const handleSendRedeemAward2 = async () => {
    setErrorMessage2('');
    setLoading(true);
    setSendRewardsSuccess(false);
    setRedeem2DisabledButton(true);

    const payload = buildRedeemAward2Payload(redeem, currentBooking, idMarivalRewardType, {
      lang: getCurrentLanguageKey(),
      ...getSessionTokens()
    });

    try {
      await submitRedeemAward2(payload);
      setLoading(false);
      setSendRewardsSuccess(true);
      loadBookings(bookingsPagination.page);
      loadRedemptionHistory();
    } catch (error) {
      setErrorMessage2(error?.payload?.errorMessage || error.message);
      setLoading(false);
      setSendRewardsSuccess(false);
      setRedeem2DisabledButton(false);
    }
  };

  const handleShowRewardsSelect = (row) => {
    setSendRewardsSuccess(false);
    setCurrentBooking(row);
    setRedeem2DisabledButton(false);
    setIdMarivalRewardType('-1');
    setErrorMessage2('');
  };

  function renderSuccessMessage() {
    if (!sendRewardsSuccess) return null;
    return (
      <div className="mt-5 flex gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-lg font-black text-white" aria-hidden>✓</div>
        <div>
          <h3 className="m-0 mb-2 text-lg font-black">Request sent successfully!</h3>
          <p className="m-0 mb-3 text-sm leading-6 text-emerald-700">
            We just sent a confirmation email. Check your inbox and, if needed, your spam folder.
          </p>
          <div className="space-y-1 text-sm leading-6 text-emerald-700">
            <div>We're reviewing your request.</div>
            <div>We'll send you updates by email.</div>
            <div>If you have any questions, please reply to the confirmation email.</div>
          </div>
        </div>
      </div>
    );
  }

  function renderRewardSelect(row) {
    if (Number(row.status) === 2 && Number(row.disabled_reedem) === 0) {
      return (
        <a href="#popup1" className="inline-flex min-h-[36px] items-center justify-center rounded-lg bg-marival-sky px-4 text-xs font-semibold text-white shadow-sm transition hover:bg-[#276cae] hover:text-white" key={row.Id_MarivalBooking} onClick={() => handleShowRewardsSelect(row)}>{'Redeem'}</a>
      );
    }

    return null;
  }

  const awardRows = [];
  for (let i = 0; i <= rows; i++) {
    awardRows.push(<RedeemAwardRow rewardTypes={rewardTypes} key={i} index={i} onChange={handleRedeemAwardChange} onRemove={handleRemoveAwardRow} />);
  }

  const noDataIndication = language.records;
  const bookingRow = currentBooking;
  const currentBookingsPage = bookingsPagination.page || 1;
  const totalBookingsPages = bookingsPagination.total_pages || 0;
  const bookingsPaginationItems = [];
  const redemptionHistoryPageSize = 10;
  const redemptionHistoryTotalPages = Math.ceil(redemptionHistory.length / redemptionHistoryPageSize) || 1;
  const currentRedemptionHistoryPage = Math.min(redemptionHistoryPage || 1, redemptionHistoryTotalPages);
  const redemptionHistoryStart = (currentRedemptionHistoryPage - 1) * redemptionHistoryPageSize;
  const redemptionHistoryRows = redemptionHistory.slice(redemptionHistoryStart, redemptionHistoryStart + redemptionHistoryPageSize);
  const redemptionHistoryPaginationItems = [];

  for (let page = 1; page <= totalBookingsPages; page++) {
    bookingsPaginationItems.push(
      <button
        key={page}
        type="button"
        className={currentBookingsPage === page ? "h-9 min-w-9 rounded-lg bg-marival-blue px-3 text-sm font-black text-white" : "h-9 min-w-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-black text-marival-blue"}
        onClick={() => changeBookingsPage(page)}
      >
        {page}
      </button>
    );
  }

  for (let historyPage = 1; historyPage <= redemptionHistoryTotalPages; historyPage++) {
    redemptionHistoryPaginationItems.push(
      <button
        key={historyPage}
        type="button"
        className={currentRedemptionHistoryPage === historyPage ? "h-8 min-w-8 rounded-lg bg-marival-blue px-3 text-xs font-black text-white" : "h-8 min-w-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-black text-marival-blue"}
        onClick={() => changeRedemptionHistoryPage(historyPage)}
      >
        {historyPage}
      </button>
    );
  }

  const columns = [{
    dataField: 'Id_BookingSource',
    text: language.bookingSource
  }, {
    dataField: 'BookingNumber',
    text: language.bookingNumber
  }, {
    dataField: 'ClientName',
    text: language.clientName
  }, {
    dataField: 'Id_MarivalHotel',
    text: language.resort
  }, {
    dataField: 'Id_MarivalRoomType',
    text: language.room
  }, {
    dataField: 'Id_Status',
    text: language.status
  }, {
    dataField: 'ValidateDate',
    text: 'Validation Date'
  }];

  if (countryUser === '167') {
    columns.push({
      dataField: 'Points',
      text: language.points
    });
  } else {
    columns.push({
      dataField: 'RewardIds',
      text: 'Action',
      render: renderRewardSelect
    });
  }

  const columnsredemtionhistory = [{
    dataField: 'RewardType',
    text: language.rewardType
  }, {
    dataField: 'Date',
    text: language.dateReddem
  }, {
    dataField: 'Status',
    text: 'Status'
  }];

  if (countryUser === '167') {
    columnsredemtionhistory.push({
      dataField: 'PointsRedeemed',
      text: language.pointsRedeemed
    });
  }

  const currentBookingRewardOptions = bookingRow ? getBookingRewardTypeOptions(rewardTypes, bookingRow.RewardIds) : [];
  const requiresGuestName = rewardTypeRequiresGuestName(rewardTypes, idMarivalRewardType);

  return (
    <div id="trackingredeem" className="mr-ui bg-[#eef1f7] px-4 py-6 sm:px-6 lg:px-8">
      <div id="popup1" className="mr-modal-overlay">
        <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-[0_24px_60px_rgba(17,24,63,0.24)]">
          <div className="mb-5 flex items-start justify-between gap-5 border-b border-slate-100 pb-4">
            <div>
              <span className="inline-flex rounded-full bg-[#eaf6fb] px-3 py-1 text-[11px] font-black uppercase tracking-wide text-marival-blue">Redeem Request</span>
              <h3 className="m-0 mt-3 text-2xl font-black text-marival-navy">Send request to redeem booking</h3>
              <p className="m-0 mt-2 text-sm leading-6 text-slate-500">Review the booking details, choose the award type, and submit your request.</p>
            </div>
            <a className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xl font-bold text-slate-500 hover:bg-slate-200 hover:text-slate-700" href="#trackingredeem">&times;</a>
          </div>

          {bookingRow ?
            <div className="space-y-4">
              <div className="grid gap-4 rounded-2xl border border-slate-200 bg-[#f6f8fc] p-4 sm:grid-cols-2">
                <div>
                  <span className="block text-[11px] font-black uppercase tracking-wide text-slate-500">Booking Number</span>
                  <span className="mt-1 block text-base font-black text-marival-navy">{bookingRow.BookingNumber}</span>
                </div>
                <div className="space-y-2">
                  <h5 className="m-0 text-xs font-black uppercase tracking-wide text-slate-600">Award Type</h5>
                  <select className={inputClass} value={idMarivalRewardType} onChange={(e) => { setIdMarivalRewardType(e.target.value); hideMessage(); }}>
                    <option key={`${bookingRow.Id_MarivalBooking}--1`} value="-1" disabled>Select</option>
                    {currentBookingRewardOptions.map((option) => (
                      <option key={`${bookingRow.Id_MarivalBooking}-${option.value}`} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <h5 className="m-0 text-xs font-black uppercase tracking-wide text-slate-600">Full Name</h5>
                <input type="text" className={inputClass} placeholder='Full name' onChange={handleRedeemFieldChange} name='redeem.RedeemFullName' value={redeem.RedeemFullName} />
              </div>

              {requiresGuestName ? (
                <div className="space-y-2">
                  <h5 className="m-0 text-xs font-black uppercase tracking-wide text-slate-600">Guest Name</h5>
                  <input type="text" className={inputClass} placeholder='Guest name' onChange={handleRedeemFieldChange} name='redeem.RedeemGuestName' value={redeem.RedeemGuestName} />
                </div>
              ) : null}

              <div className="flex justify-end">
                <button disabled={redeem2DisabledButton} className="min-h-[44px] rounded-xl bg-marival-sky px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-[#276cae] disabled:cursor-not-allowed disabled:opacity-60" onClick={handleSendRedeemAward2}><span>{language.sendbtn}</span></button>
              </div>

              {loading ? <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-600">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-marival-blue border-t-transparent"></div>
                <p className="m-0">Please wait a moment, we are sending your request <span className="dots"><span>.</span><span>.</span><span>.</span></span></p>
              </div>
                : ''}

              {renderSuccessMessage()}
              {errorMessage2 ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">{errorMessage2}</div>
              ) : null}
            </div>
            : null}
        </div>
      </div>

      <div className="mx-auto max-w-7xl">
        {/* Bloque de puntos: solo para agentes de México (CountryUser === '167') */}
        {countryUser === '167' ? (
          <div className="grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,2fr)]">
            <div className="flex min-h-[112px] items-center rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h1 className="m-0 text-xl font-normal uppercase text-marival-navy">{language.text1}</h1>
            </div>
            <div className="flex min-h-[112px] items-center rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="space-y-2">
                <h2 className="m-0 flex items-baseline gap-3 text-2xl font-normal text-marival-sky">
                  <span>{pointsPending}</span>
                  <small className="text-xs font-normal uppercase text-slate-500">{language.pending}</small>
                </h2>
                <h2 className="m-0 flex items-baseline gap-3 text-2xl font-normal text-marival-sky">
                  <span>{pointsAvailable}</span>
                  <small className="text-xs font-normal uppercase text-slate-500">{language.Total_Points}</small>
                </h2>
              </div>
            </div>
            <div className="flex min-h-[112px] items-center rounded-2xl bg-marival-coral p-7 text-white shadow-coral">
              <h3 className="m-0 max-w-2xl text-2xl font-semibold leading-tight">{language.text2}</h3>
            </div>
          </div>
        ) : null}

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <p className="m-0 mb-5 text-sm leading-6 text-slate-500">{language.text2}</p>
          <SimpleTable columns={columns} rows={bookings} keyField='Id_MarivalBooking' noDataIndication={noDataIndication} />
        </section>

        {totalBookingsPages > 1 ? (
          <div id="bookings-pagination" className="mt-4 flex flex-wrap justify-center gap-2">
            {bookingsPaginationItems}
          </div>
        ) : null}

        {SHOW_FREE_NIGHTS_TERMS ? (
        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 text-sm leading-7 text-slate-600 shadow-sm">
          {countryUser === '167' ?
            <>
              <p className="m-0">{language.redeem_a}</p>
              <p className="m-0 font-black text-marival-navy">{language.redeem_b}</p>
              <p className="m-0">{language.redeem_c}</p>
              <p className="m-0">{language.redeem_d}</p>
              <p className="m-0">{language.redeem_e}</p>
              <p className="m-0 font-black text-marival-navy">{language.redeem_f}</p>
              <p className="m-0">{language.redeem_g}</p>
              <p className="m-0">{language.redeem_h}</p>
            </>
            :
            <>
              <p className="m-0">{language.redeem_a_1}</p>
              <p className="m-0 font-black text-marival-navy">{language.redeem_b_1}</p>
              <p className="m-0">{language.redeem_c_1}</p>
              <p className="m-0">{language.redeem_d_1}</p>
              <p className="m-0">{language.redeem_e_1}</p>
              <p className="m-0 font-black text-marival-navy">{language.redeem_f_1}</p>
              <p className="m-0">{language.redeem_g_1}</p>
              <p className="m-0">{language.redeem_h_1}</p>
            </>}
        </section>
        ) : null}

        <div className="mt-6 grid items-start gap-6 lg:grid-cols-2">
          <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="m-0 mb-5 text-lg font-normal text-marival-navy">{language.remptioHistory}</h2>
            <SimpleTable columns={columnsredemtionhistory} rows={redemptionHistoryRows} keyField='Id_MarivalRedemtionHistroy' noDataIndication={noDataIndication} compact />
            {redemptionHistoryTotalPages > 1 ? (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-black text-marival-blue disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={currentRedemptionHistoryPage === 1}
                  onClick={() => changeRedemptionHistoryPage(currentRedemptionHistoryPage - 1)}
                >
                  Prev
                </button>
                <div className="flex flex-wrap justify-center gap-2">
                  {redemptionHistoryPaginationItems}
                </div>
                <button
                  type="button"
                  className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-black text-marival-blue disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={currentRedemptionHistoryPage === redemptionHistoryTotalPages}
                  onClick={() => changeRedemptionHistoryPage(currentRedemptionHistoryPage + 1)}
                >
                  Next
                </button>
              </div>
            ) : null}
          </section>

          <section className="row_redeemaward self-start rounded-2xl bg-marival-blue p-6 text-white shadow-[0_18px_36px_rgba(74,166,207,0.30)]">
            <h2 className="m-0 text-2xl font-normal">{language.awardboxText1}</h2>
            <p className="m-0 mt-2 text-sm leading-6 text-white/80">{language.awardboxText6}</p>

            <div className="mt-5 space-y-4">
              {awardRows}
            </div>

            <button type="button" className="mt-5 inline-flex min-h-[40px] items-center justify-center rounded-full bg-white/15 px-5 text-sm font-black text-white transition hover:bg-white/20" onClick={handleAddAwardRow}>
              <span className="mr-2 text-lg leading-none">+</span>
              <span>{language.awardboxText4}</span>
            </button>

            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <input type="text" className={darkInputClass} placeholder={language.awardboxFielName} onChange={handleRedeemFieldChange} name='redeem.RedeemFullName' value={redeem.RedeemFullName} />
              <input type="text" className={darkInputClass} placeholder={language.awardboxFielEmail} onChange={handleRedeemFieldChange} name='redeem.RedeemEmail' value={redeem.RedeemEmail} />
            </div>

            {countryUser === '167' ?
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <input type="text" className={darkInputClass} placeholder={language.awardboxFielStreet} onChange={handleRedeemFieldChange} name='redeem.Street' value={redeem.Street} />
                <input type="text" className={darkInputClass} placeholder={language.awardboxFielApt} onChange={handleRedeemFieldChange} name='redeem.Apt' value={redeem.Apt} />
                <input type="text" className={darkInputClass} placeholder={language.awardboxFielCity} onChange={handleRedeemFieldChange} name='redeem.City' value={redeem.City} />
                <input type="text" className={darkInputClass} placeholder={language.awardboxFielProvince} onChange={handleRedeemFieldChange} name='redeem.Province' value={redeem.Province} />
                <input type="text" className={darkInputClass} placeholder={language.awardboxFielZip} onChange={handleRedeemFieldChange} name='redeem.Zipcode' value={redeem.Zipcode} />
                <input type="text" className={darkInputClass} placeholder={language.awardboxFielCountry} onChange={handleRedeemFieldChange} name='redeem.Country' value={redeem.Country} />
                <input type="text" className={darkInputClass} placeholder={language.awardboxFielCurp} onChange={handleRedeemFieldChange} name='redeem.Curp' value={redeem.Curp} />
                <input type="text" className={darkInputClass} placeholder={language.awardboxFielRfc} onChange={handleRedeemFieldChange} name='redeem.Rfc' value={redeem.Rfc} />
                {isCash ?
                  <>
                    <input type="text" className={darkInputClass} placeholder={language.awardboxFielBankName} onChange={handleRedeemFieldChange} name='redeem.BankName' value={redeem.BankName} />
                    <input type="text" className={darkInputClass} placeholder={language.awardboxFielBankClabe} onChange={handleRedeemFieldChange} name='redeem.BankClabe' value={redeem.BankClabe} />
                    <input type="text" className={darkInputClass} placeholder={language.awardboxFielBankAccount} onChange={handleRedeemFieldChange} name='redeem.BankAccount' value={redeem.BankAccount} />
                    <input type="text" className={darkInputClass} placeholder={language.awardboxFielBankBeneficiary} onChange={handleRedeemFieldChange} name='redeem.BankBeneficiary' value={redeem.BankBeneficiary} />
                  </> : null}
              </div>
              : null}

            <div className="mt-5 flex justify-end">
              <button disabled={loading} className="min-h-[44px] rounded-xl bg-marival-navy px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-marival-ink disabled:cursor-not-allowed disabled:opacity-70" onClick={handleSendRedeemAward}><span>{loading ? 'Enviando...' : language.sendbtn}</span></button>
            </div>

            {loading ? (
              <div className="mt-4 flex items-center gap-3 rounded-xl border border-white/20 bg-white/95 px-4 py-3 text-sm font-bold text-slate-600">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-marival-orange border-t-transparent"></div>
                <div>
                  <span className="block font-black text-marival-navy">Enviando solicitud</span>
                  <span className="block text-xs">Estamos procesando tu redencion, espera un momento.</span>
                </div>
              </div>
            ) : null}

            {redeemBoxMessage ? (
              <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-700">{redeemBoxMessage}</div>
            ) : null}
            <p className="m-0 mt-4 text-xs font-bold leading-5 text-white">{language.note}</p>
            <p className="m-0 mt-2 text-xs font-bold leading-5 text-white">{language.note2}</p>
            {redeemBoxError ? (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-600">{redeemBoxError}</div>
            ) : null}
          </section>

          {countryUser !== '167' ?
            <section className="rounded-2xl border border-orange-200 border-l-4 border-l-marival-orange bg-white p-6 shadow-sm lg:col-start-2">
              <h3 className="m-0 mb-4 text-xl font-black text-marival-orange">{language.processingNoticeTitle}</h3>
              <p className="m-0 mb-3 text-sm leading-7 text-slate-600">{language.processingNoticeLead}</p>
              <p className="m-0 mb-4 text-sm leading-7 text-slate-600">{language.processingNoticeBody}</p>
              <div className="rounded-2xl border border-orange-100 bg-orange-50 p-4">
                <strong className="block text-sm text-marival-orange">{language.processingNoticeImportantTitle}</strong>
                <p className="m-0 mt-2 text-sm leading-7 text-slate-600">{language.processingNoticeImportantBody}</p>
              </div>
              {/* Las fechas restringidas solo aplican a las noches de cortesía,
                  así que se ocultan con el mismo flag. */}
              {SHOW_FREE_NIGHTS_TERMS ? (
                <p className="m-0 mt-4 border-t border-slate-100 pt-4 text-sm leading-7 text-slate-500">{language.processingNoticeBlackout}</p>
              ) : null}
            </section>
            : null}
        </div>
      </div>
    </div>
  );
}

export default TrackRedeem;
